import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Trophy, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { pickMessage } from "@/lib/messages";

/**
 * Stage 2.2: premium wave-themed celebration overlay shown once per
 * completed challenge (state persisted server-side by useChallengeCelebration).
 */
export const ChallengeCelebration = ({ celebration, onDismiss }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const go = (path, state) => {
    onDismiss();
    navigate(path, state ? { state } : undefined);
  };

  return (
    <AnimatePresence>
      {celebration && (
        <motion.div
          data-testid="challenge-celebration-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-center justify-center px-5 bg-slate-950/70 backdrop-blur-md"
          onClick={onDismiss}
        >
          {/* Ripple rings — the wave moment */}
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden="true"
          >
            <div className="ripple-ring absolute h-80 w-80 rounded-full border border-cyan-300/25" />
            <div className="ripple-ring-delay absolute h-80 w-80 rounded-full border border-cyan-300/15" />
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm rounded-3xl bg-white/[0.06] border border-white/[0.12] backdrop-blur-xl p-6 shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
          >
            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(500px_circle_at_50%_0%,rgba(34,211,238,0.12),transparent_60%)]" />
            <div className="relative">
              {/* Brand header — never translated */}
              <div className="flex items-center gap-2">
                <Waves size={14} className="text-cyan-300" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-cyan-300/90 font-semibold">
                  Blue Ocean Motion
                </span>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-2xl bg-cyan-300/15 border border-cyan-300/35 flex items-center justify-center shadow-[0_0_50px_rgba(34,211,238,0.25)]">
                  <Trophy size={30} className="text-cyan-300" />
                </div>
                <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-cyan-300/80 font-medium">
                  {t("celebration.kicker")}
                </p>
                <h2
                  data-testid="celebration-title"
                  className="mt-1 text-2xl font-semibold tracking-tight"
                >
                  {t("celebration.title")}
                </h2>
                <p
                  data-testid="celebration-challenge-name"
                  className="mt-2 text-base font-medium text-white/90"
                >
                  {t(`challengesContent.${celebration.key}.title`)}
                </p>
                <p
                  data-testid="celebration-result"
                  className="mt-1 text-sm text-[hsl(var(--muted-foreground))]"
                >
                  {t("celebration.resultLabel")}:{" "}
                  <span className="text-cyan-300 font-semibold [font-variant-numeric:tabular-nums]">
                    {celebration.target} {t(`challengesContent.${celebration.key}.unit`)}
                  </span>
                </p>
                <p
                  data-testid="celebration-sentence"
                  className="motion-message mt-4 text-sm leading-relaxed font-medium"
                >
                  {pickMessage(t("celebration.sentencePool"), celebration.key.length)}
                </p>
              </div>

              <div className="mt-6 space-y-2.5">
                <Button
                  data-testid="celebration-view-challenge"
                  onClick={() => go("/community", { tab: "challenges" })}
                  className="w-full py-5 rounded-xl bg-cyan-300 text-slate-950 font-semibold hover:bg-cyan-200 active:scale-[0.98]"
                >
                  {t("celebration.viewChallenge")}
                </Button>
                <div className="grid grid-cols-2 gap-2.5">
                  <Button
                    data-testid="celebration-view-progress"
                    onClick={() => go("/progress")}
                    variant="outline"
                    className="py-5 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white active:scale-[0.98]"
                  >
                    {t("celebration.viewProgress")}
                  </Button>
                  <Button
                    data-testid="celebration-back-community"
                    onClick={() => go("/community", { tab: "feed" })}
                    variant="outline"
                    className="py-5 rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white active:scale-[0.98]"
                  >
                    {t("celebration.backToCommunity")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
